<div class="page">
  <!-- Header -->
  <div class="header">
    <div class="title-block">
      <h1 class="title">Teams</h1>
      <div class="subtitle">Manage teams and view employee details</div>
    </div>

    @if (hasCompany && workforce.mode === 'snapshot') {
      <button mat-raised-button color="primary" class="create-btn" (click)="onCreateTeam()">
        <mat-icon>group_add</mat-icon>Create new team
      </button>
    }
    @if (hasCompany && canUseLegacyCensus) {
      <button mat-raised-button color="primary" class="create-btn" (click)="onCreateTeam()">
        <mat-icon>add</mat-icon>
        Create new team
      </button>
    }
  </div>

  @if (snapshotSettingsCompanyId) {
    <app-payroll-workforce-progress [creation]="teamCreation"></app-payroll-workforce-progress>
  }

  @if (!isInitialCompanyCheckComplete) {
    <div class="page-loading">
      <mat-spinner [diameter]="32"></mat-spinner>
      <span>Loading</span>
    </div>
  }

  @if (isInitialCompanyCheckComplete && !hasCompany && !isCompanyScopedWorkspace) {
    <section class="setup-cta">
      <div class="setup-cta-content">
        <mat-icon>business</mat-icon>
        <div class="setup-cta-copy">
          <h2>Create your first company</h2>
          <p>To use Teams, create a company first in Company > Company.</p>
        </div>
      </div>
      <button mat-raised-button color="primary" type="button" (click)="onGoToCompanyTab()">
        Go to Company
      </button>
    </section>
  }

  @if (isInitialCompanyCheckComplete && hasCompany && isTerminationFeatureEnabled && terminationReviewLoadError) {
    <section
      class="termination-review-error"
      role="status"
      >
      <div>
        <mat-icon>info_outline</mat-icon>
        <span>{{ terminationReviewLoadError }}</span>
      </div>
      <button mat-stroked-button type="button" (click)="retryTerminationReview()">Retry</button>
    </section>
  }

  @if (showTerminationReviewCard) {
    <section
      class="termination-review-card"
      >
      <div class="termination-review-card__summary">
        <div class="termination-review-card__icon" aria-hidden="true">
          <mat-icon>assignment_ind</mat-icon>
        </div>
        <div class="termination-review-card__copy">
          <h2>{{ terminationReviewCountLabel }} termination {{ terminationCases.length === 1 && !hasMoreTerminationCases ? 'case' : 'cases' }} to review</h2>
          <p>Review UKG Ready termination evidence and company impact previews. No offboarding changes have been made.</p>
        </div>
        <button
          mat-stroked-button
          type="button"
          (click)="toggleTerminationReviewList()"
          [attr.aria-expanded]="isTerminationReviewListOpen"
          aria-controls="termination-review-list"
          >
          {{ isTerminationReviewListOpen ? 'Hide reviews' : 'Review terminations' }}
        </button>
        <button
          mat-stroked-button
          type="button"
          (click)="toggleResolvedHistory()"
          [attr.aria-expanded]="isResolvedHistoryOpen"
          aria-controls="resolved-termination-history"
          >
          {{ isResolvedHistoryOpen ? 'Hide resolved' : 'Show resolved' }}
        </button>
      </div>
      @if (isTerminationReviewListOpen) {
        <div
          id="termination-review-list"
          class="termination-review-list"
          >
          @for (terminationCase of terminationCases; track trackByTerminationCase($index, terminationCase)) {
            <button
              class="termination-review-list__item"
              type="button"
              (click)="openTerminationReview(terminationCase, $event)"
              [attr.aria-label]="terminationReviewLabel(terminationCase) + ' for ' + terminationEmployeeIdentity(terminationCase).name + '. Open read-only impact preview.'"
              >
              <div class="termination-review-list__identity">
                <strong>{{ terminationEmployeeIdentity(terminationCase).name }}</strong>
                @if (terminationEmployeeIdentity(terminationCase).email) {
                  <span>{{ terminationEmployeeIdentity(terminationCase).email }}</span>
                }
                <span>{{ terminationCase.providerLabel }} · {{ terminationCase.matchStatus | titlecase }} match</span>
              </div>
              <div class="termination-review-list__state">
                <span
                  class="termination-marker"
                  [ngClass]="terminationReviewMarkerClass(terminationCase)"
                  >
                  <mat-icon aria-hidden="true">{{ terminationCase.lifecycleStatus === 'scheduled' ? 'event' : (terminationReviewLabel(terminationCase) === 'Termination review' ? 'assignment_late' : 'rule') }}</mat-icon>
                  {{ terminationReviewLabel(terminationCase) }}
                </span>
                <span>{{ (terminationCase.effectiveTerminationDate | date:'mediumDate':'UTC') || 'Date unavailable' }}</span>
                @if (terminationCase.requiresManualReview) {
                  <span>Manual review required</span>
                }
              </div>
            </button>
          }
          @if (hasMoreTerminationCases) {
            <div class="termination-review-list__footer">
              <span>{{ terminationReviewCountLabel }} cases loaded. More reviews are available.</span>
              <button
                mat-stroked-button
                type="button"
                [disabled]="isTerminationReviewLoadingMore"
                (click)="loadMoreTerminationCases()"
                >
                {{ isTerminationReviewLoadingMore ? 'Loading…' : 'Load more' }}
              </button>
            </div>
          }
        </div>
      }
      @if (isResolvedHistoryOpen) {
        <div
          id="resolved-termination-history"
          class="resolved-termination-history"
          >
          <div class="resolved-termination-history__heading">
            <div>
              <h3>Resolved termination history</h3>
              <p>Completed reviews, baseline history, and cases administratively marked resolved.</p>
            </div>
          </div>
          @if (isResolvedHistoryLoading) {
            <div class="resolved-termination-history__loading">
              <mat-spinner [diameter]="24"></mat-spinner>
              <span>Loading resolved history</span>
            </div>
          }
          @if (!isResolvedHistoryLoading && resolvedHistoryLoadError) {
            <div class="termination-review-error" role="status">
              <div><mat-icon>info_outline</mat-icon><span>{{ resolvedHistoryLoadError }}</span></div>
              <button mat-stroked-button type="button" (click)="retryResolvedTerminationHistory()">Retry</button>
            </div>
          }
          @if (!isResolvedHistoryLoading && !resolvedHistoryLoadError && !resolvedTerminationCases.length) {
            <p class="resolved-termination-history__empty">
              No resolved termination cases were found.
            </p>
          }
          @for (terminationCase of resolvedTerminationCases; track trackByTerminationCase($index, terminationCase)) {
            <button
              class="termination-review-list__item"
              type="button"
              (click)="openTerminationReview(terminationCase, $event)"
              [attr.aria-label]="resolvedTerminationLabel(terminationCase) + ' for ' + terminationEmployeeIdentity(terminationCase).name + '. Open read-only evidence.'"
              >
              <div class="termination-review-list__identity">
                <strong>{{ terminationEmployeeIdentity(terminationCase).name }}</strong>
                @if (terminationEmployeeIdentity(terminationCase).email) {
                  <span>{{ terminationEmployeeIdentity(terminationCase).email }}</span>
                }
                <span>{{ terminationCase.providerLabel }} · {{ terminationCase.matchStatus | titlecase }} match</span>
              </div>
              <div class="termination-review-list__state">
                <span class="termination-marker termination-marker--resolved">
                  <mat-icon aria-hidden="true">{{ terminationCase.lifecycleStatus === 'completed' ? 'task_alt' : 'check_circle_outline' }}</mat-icon>
                  {{ resolvedTerminationLabel(terminationCase) }}
                </span>
                <span>{{ (resolvedTerminationDate(terminationCase) | date:'mediumDate':'UTC') || 'Date unavailable' }}</span>
              </div>
            </button>
          }
          @if (hasMoreResolvedTerminationCases) {
            <div class="termination-review-list__footer">
              <span>More resolved cases are available.</span>
              <button
                mat-stroked-button
                type="button"
                [disabled]="isResolvedHistoryLoadingMore"
                (click)="loadMoreResolvedTerminationCases()"
                >
                {{ isResolvedHistoryLoadingMore ? 'Loading…' : 'Load more' }}
              </button>
            </div>
          }
        </div>
      }
    </section>
  }

  <!-- Tabs -->
  @if (isInitialCompanyCheckComplete && hasCompany) {
    <mat-tab-group [selectedIndex]="selectedTabIndex" (selectedIndexChange)="onTabChange($event)" class="tabs">
      <mat-tab label="Teams">
        @if (!isCompanyScopedWorkspace && !isHealthyPayrollConnection) {
          <div class="setup-cta setup-cta-inline">
            <div class="setup-cta-content">
              <mat-icon>{{ isPayrollConnectionErrored ? 'error_outline' : 'link_off' }}</mat-icon>
              <div class="setup-cta-copy">
                <h2>{{ payrollConnectionStateTitle }}</h2>
                <p>{{ payrollConnectionStateMessage }}</p>
              </div>
            </div>
            @if (!isCompanyScopedWorkspace) {
              <button mat-raised-button color="primary" type="button" (click)="onGoToCompanyConnections()">
                Go to Company Connections
              </button>
            }
          </div>
        }
        @if (hasPendingGroupProductOnboarding) {
          <div class="setup-cta setup-cta-inline group-product-setup-banner">
            <div class="setup-cta-content">
              <mat-icon>assignment_late</mat-icon>
              <div class="setup-cta-copy">
                <h2>Group plan enrollment in progress</h2>
                <p>{{ pendingGroupProductBannerMessage }}</p>
              </div>
            </div>
          </div>
        }
        <!-- Content -->
        <div class="content">
          @if (teams.length || pendingSnapshotTeams.length || !isTeamsDataLoaded) {
            <div class="section-header">
              <h2 class="section-title">Current teams</h2>
              <div class="section-meta">
                <span>{{ teams.length }} teams@if (totalMembers !== null) {
                  <span> • {{ totalMembers }} total members</span>
                }</span>
              </div>
            </div>
            <div class="teams-grid">
              @for (operation of pendingSnapshotTeams; track operation.operationId) {
                <mat-card class="team-card">
                  <div class="team-name">{{ operation.pendingName || 'New team' }}</div>
                  <app-payroll-workforce-progress [creation]="teamCreation" [scoped]="true" [operation]="operation"></app-payroll-workforce-progress>
                </mat-card>
              }
              @for (team of teams; track team) {
                <mat-card class="team-card" (click)="onTeamClick(team)">
                  <div class="card-top">
                    <div class="avatar">
                      <mat-icon>group</mat-icon>
                    </div>
                    <div class="card-title-area">
                      <div class="team-name">{{ team.name }}</div>
                      <div class="created">{{ formatCreatedAt(team.createdAt) }}</div>
                    </div>
                    <button
                      mat-icon-button
                      [matMenuTriggerFor]="teamMenu"
                      aria-label="Open team menu"
                      class="menu-btn"
                      (click)="$event.stopPropagation()"
                      >
                      <mat-icon>more_vert</mat-icon>
                    </button>
                    <mat-menu #teamMenu="matMenu">
                      @if (canUseLegacyCensus || snapshotSettingsCompanyId) {
                        <button mat-menu-item [disabled]="teamCreation.teamBusy(team.id)" (click)="onEditTeam(team)">
                          <mat-icon>edit</mat-icon>
                          <span>Edit team</span>
                        </button>
                      }
                      @if (canUseLegacyCensus) {
                        <button mat-menu-item (click)="onDuplicateTeam(team)">
                          <mat-icon>content_copy</mat-icon>
                          <span>Duplicate team</span>
                        </button>
                      }
                      <button mat-menu-item class="danger" (click)="onDeleteTeam(team)">
                        <mat-icon>delete</mat-icon>
                        <span>Delete team</span>
                      </button>
                    </mat-menu>
                  </div>
                  <app-payroll-workforce-progress [creation]="teamCreation" [scoped]="true" [operation]="teamCreation.forTeam(team.id)"></app-payroll-workforce-progress>
                  <div class="card-bottom">
                    <div class="meta">
                      <mat-icon class="meta-icon">group</mat-icon>
                      <span>{{ knownMemberCount(team) === null ? 'View members in Team Details' : team.membersCount + ' members' }}</span>
                    </div>
                    <div class="meta price">
                      <mat-icon class="meta-icon">attach_money</mat-icon>
                      <span>${{ team.amount }} <span class="muted">/mo</span></span>
                    </div>
                  </div>
                </mat-card>
              }
            </div>
          } @else {
            <section class="teams-empty-state">
              <div class="teams-empty-state__icon">
                <mat-icon>group_add</mat-icon>
              </div>
              <h2>No teams created yet</h2>
              @if (isHealthyPayrollConnection) {
                <p>
                  Create your first team to assign employees and manage benefits.
                </p>
              } @else {
                <p>Create your first team now, then add members by email or spreadsheet upload.</p>
              }
              @if (workforce.mode === 'snapshot') {
                <button mat-raised-button color="primary" type="button" (click)="onCreateTeam()"><mat-icon>group_add</mat-icon>Create new team</button>
              }
              @if (canUseLegacyCensus) {
                <button mat-raised-button color="primary" type="button" (click)="onCreateTeam()">
                  <mat-icon>add</mat-icon>
                  Create new team
                </button>
              }
            </section>
          }
        </div>
      </mat-tab>
      <mat-tab label="Team Details">
        @if (teams.length || !isTeamsDataLoaded) {
          @if (selectedTeam; as team) {
            <div class="team-details">
              <app-payroll-workforce-progress [creation]="teamCreation" [scoped]="true" [operation]="teamCreation.forTeam(team.id)"></app-payroll-workforce-progress>
              @if (!isCompanyScopedWorkspace && !isHealthyPayrollConnection) {
                <div class="setup-cta setup-cta-inline connection-state-inline">
                  <div class="setup-cta-content">
                    <mat-icon>{{ isPayrollConnectionErrored ? 'error_outline' : 'link_off' }}</mat-icon>
                    <div class="setup-cta-copy">
                      <h2>{{ payrollConnectionStateTitle }}</h2>
                      <p>{{ payrollConnectionStateMessage }}</p>
                    </div>
                  </div>
                  @if (!isCompanyScopedWorkspace) {
                    <button mat-stroked-button type="button" (click)="onGoToCompanyConnections()">
                      Company Connections
                    </button>
                  }
                </div>
              }
              @if (hasPendingGroupProductOnboarding) {
                <div class="setup-cta setup-cta-inline group-product-setup-banner">
                  <div class="setup-cta-content">
                    <mat-icon>assignment_late</mat-icon>
                    <div class="setup-cta-copy">
                      <h2>Group plan enrollment in progress</h2>
                      <p>{{ pendingGroupProductBannerMessage }}</p>
                    </div>
                  </div>
                </div>
              }
              <!-- Select team row -->
              <div class="select-row">
                <div class="select-label">Select team:</div>
                <mat-form-field appearance="outline" class="team-select">
                  <mat-select [value]="selectedTeamId" (selectionChange)="onSelectedTeamChange($event.value)">
                    @for (t of teams; track t) {
                      <mat-option [value]="t.id">{{ t.name }}</mat-option>
                    }
                  </mat-select>
                </mat-form-field>
              </div>
              <!-- Team summary card -->
              <mat-card class="big-card">
                <app-team-summary
                  [name]="team.name"
                  [amount]="team.amount ?? 0"
                  [membersCount]="team.membersCount ?? null"
                  [canEdit]="canUseLegacyCensus || !!snapshotSettingsCompanyId"
                  (edit)="onEditSelectedTeamDetails()">
                </app-team-summary>
                @if (snapshotSettingsCompanyId) {
                  <div class="snapshot-team-actions">
                    <button mat-stroked-button type="button" [disabled]="teamCreation.teamBusy(team.id)" (click)="onEditMembersFromDetails()">Edit Members</button>
                    <button mat-stroked-button type="button" [disabled]="teamCreation.teamBusy(team.id)" (click)="onEditTeam(team)">Edit team</button>
                  </div>
                }
                <mat-divider></mat-divider>
                @if (canShowSelectedTeamAutoAddControls) {
                  <div class="toggle-row">
                    <mat-slide-toggle
                      [ngModel]="team.autoAddNewMembers"
                [disabled]="
                  !canUseSelectedTeamAutoAdd ||
                  isAutoAddSaving ||
                  isAutoAddEligibilityLoading ||
                  (!selectedTeamAutoAddEligible && !team.autoAddNewMembers)
                "
                      (ngModelChange)="onSelectedTeamAutoAddChanged($event)"
                      >
                      Auto-add new Team Members
                    </mat-slide-toggle>
                    <button
                      mat-icon-button
                      class="help"
                      aria-label="Auto-add info"
                      [matTooltip]="selectedTeamAutoAddTooltip"
                      >
                      <mat-icon>help_outline</mat-icon>
                    </button>
                  </div>
                  @if (isAutoAddEligibilityLoading) {
                    <div class="toggle-helper">
                      Checking auto-add eligibility...
                    </div>
                  }
                  @if (!isAutoAddEligibilityLoading && !selectedTeamAutoAddEligible) {
                    <div class="toggle-helper">
                      {{ selectedTeamAutoAddReason }}
                    </div>
                  }
                }
                @if (shouldShowSelectedTeamFundingTiming) {
                  <div class="funding-timing-section">
                    <mat-divider></mat-divider>
                    <button
                      type="button"
                      class="funding-timing-toggle"
                      (click)="toggleFundingTimingExpanded()"
                      [attr.aria-expanded]="isFundingTimingExpanded"
                      >
                      <div class="funding-timing-toggle-copy">
                        <div class="funding-timing-title">Funding timing</div>
                        <div class="funding-timing-subtitle">
                          {{ selectedTeamFundingTimingSummary }} • {{ selectedTeamFundingMethodLabel }}
                        </div>
                      </div>
                      <mat-icon class="funding-timing-toggle-icon">
                        {{ isFundingTimingExpanded ? 'expand_less' : 'expand_more' }}
                      </mat-icon>
                    </button>
                    @if (isFundingTimingExpanded) {
                      <div class="funding-timing-body">
                        <app-monthly-topup-schedule [teamId]="selectedTeam.id" [fundingVersion]="teamCreation.fundingVersionForTeam(selectedTeam.id)" [settingsVersion]="selectedTeam.amount + ':' + selectedTeam.topupDisabled + ':' + selectedTeam.defaultPaymentMethod" [disabled]="!canEditSelectedTeamFundingTiming"></app-monthly-topup-schedule>
                        @if (shouldShowSelectedTeamFundingTimingControls) {
                          <div class="funding-timing-header">
                            <div class="funding-timing-copy">
                              <div class="funding-timing-subtitle">
                                Default funding method:
                                <span class="funding-timing-method">{{ selectedTeamFundingMethodLabel }}</span>
                              </div>
                            </div>
                            <mat-radio-group
                              class="funding-timing-options"
                              [ngModel]="selectedTeamFundingTimingValue"
                              [disabled]="!canEditSelectedTeamFundingTiming || isFundingTimingSaving"
                              (ngModelChange)="onSelectedTeamFundingTimingChanged($event)"
                              >
                              <mat-radio-button value="fund_now">Fund right away</mat-radio-button>
                              <mat-radio-button value="next_month">Start next cycle</mat-radio-button>
                            </mat-radio-group>
                            @if (isFundingTimingSaving) {
                              <mat-spinner [diameter]="20"></mat-spinner>
                            }
                          </div>
                        }
                        @if (!shouldShowSelectedTeamFundingTimingControls) {
                          <div class="funding-timing-empty-state">
                            <div class="funding-timing-helper funding-timing-helper--compact">
                              {{ selectedTeamFundingTimingDisabledReason }}
                            </div>
                            @if (selectedTeamFundingMethodCtaLabel) {
                              <button
                                mat-button
                                type="button"
                                class="funding-timing-cta"
                                [disabled]="isFundingMethodActionLoading"
                                (click)="onSelectedTeamFundingMethodCtaClicked()"
                                >
                                {{ selectedTeamFundingMethodCtaLabel }}
                              </button>
                            }
                          </div>
                        }
                        @if (shouldShowSelectedTeamFundingTimingControls) {
                          <div class="funding-timing-helper">
                            Fund right away charges the team now and prorates through the next scheduled top-up. Start next cycle
                            waits until the scheduled monthly payment.
                          </div>
                        }
                        @if (shouldShowSelectedTeamFundingPrompt) {
                          <div class="funding-members-callout">
                            <div class="funding-members-copy">
                              {{ selectedTeamFundingPromptMessage }}
                            </div>
                            @if (canFundSelectedTeamAllowancesNow) {
                              <button
                                mat-stroked-button
                                type="button"
                                class="funding-members-button"
                                [disabled]="isFundingMembersLoading || monthlyTopupSchedule?.fundingBlocked"
                                (click)="fundSelectedTeamAllowances()"
                                >
                                Fund Member Allowances
                              </button>
                            }
                          </div>
                        }
                        @if (selectedTeamFundingPromptHelper) {
                          <div class="funding-timing-helper">
                            {{ selectedTeamFundingPromptHelper }}
                          </div>
                        }
                      </div>
                    }
                  </div>
                }
              </mat-card>
              <!-- Employees accordion card -->
              <mat-expansion-panel
                class="employees-panel"
                [expanded]="isEmployeesPanelExpanded"
                (opened)="isEmployeesPanelExpanded = true"
                (closed)="isEmployeesPanelExpanded = false"
                (afterExpand)="onEmployeesPanelAfterExpand()"
                >
                <mat-expansion-panel-header>
                  <mat-panel-title>
                    <div class="panel-title">
                      <div class="icon-square orange">
                        <mat-icon>person</mat-icon>
                      </div>
                      <div>
                        <div class="panel-name">Employees</div>
                        <div class="panel-sub">{{ knownMemberCount(team) === null ? 'Member count unavailable' : team.membersCount + ' team members' }}</div>
                      </div>
                    </div>
                  </mat-panel-title>
                  <mat-panel-description>
                    @if (canUseLegacyCensus) {
                      <button
                        mat-icon-button
                        class="pencil"
                        [disabled]="teamCreation.teamBusy(team.id)"
                        (click)="$event.stopPropagation(); onEditMembersFromDetails()"
                        aria-label="Edit employees"
                        >
                        <mat-icon>edit</mat-icon>
                      </button>
                    }
                  </mat-panel-description>
                </mat-expansion-panel-header>
                <div class="employees-expanded">
                  <!-- Applied filters -->
                  @if (selectedTeamFilterChips.length) {
                    <div class="filters">
                      <div class="filters-label">APPLIED FILTERS</div>
                      <div class="filters-chips">
                        <!-- Derived display-only chips can contain duplicate labels/values. -->
                        @for (chip of selectedTeamFilterChips; track $index) {
                          <div class="filter-chip">
                            {{ chip.display }}
                          </div>
                        }
                      </div>
                    </div>
                  }
                  @if (showGuidedTerminationBanner) {
                    <div
                      #guidedTerminationBanner
                      class="guided-termination-banner"
                      tabindex="-1"
                      >
                      <div>
                        <strong>Guided termination review</strong>
                        <span>{{ guidedTerminationMessage }}</span>
                      </div>
                      <button mat-stroked-button type="button" (click)="openGuidedTerminationReview()">
                        Open termination review
                      </button>
                    </div>
                  }
                  <!-- Team members -->
                  <div class="members">
                    <div class="members-label">TEAM MEMBERS ({{ selectedTeamMembers.length }})</div>
                    @for (m of selectedTeamMembers; track trackByMember($index, m)) {
                      <div
                        #selectedTeamMemberRow
                        class="member-row"
                        [class.member-row--termination-guided]="isGuidedTerminationMember(m)"
                        role="button"
                        tabindex="0"
                        (click)="onSelectedTeamMemberDetails(m)"
                        (keydown.enter)="onSelectedTeamMemberDetails(m)"
                        (keydown.space)="onSelectedTeamMemberDetails(m); $event.preventDefault()"
                        >
                        <div class="member-main">
                          <div class="member-header">
                            <div class="member-identity">
                              <div class="member-name">{{ memberDisplayName(m) }}</div>
                              <div class="member-email">{{ memberEmail(m) || '—' }}</div>
                            </div>
                          </div>
                          @if (memberSummary(m); as summary) {
                            <div class="member-summary">
                              {{ summary }}
                            </div>
                          } @else {
                            <div class="member-summary member-summary--empty">No additional employee details</div>
                          }
                        </div>
                        <div class="member-meta">
                          @if (terminationCaseForMember(m); as terminationCase) {
                            <button
                              mat-stroked-button
                              type="button"
                              class="termination-marker termination-marker--button"
                              [ngClass]="terminationReviewMarkerClass(terminationCase)"
                              [attr.aria-label]="terminationMarkerAriaLabel(m, terminationCase)"
                              (click)="openTerminationReview(terminationCase, $event)"
                              (keydown.enter)="stopTerminationMarkerKeyboardEvent($event)"
                              (keydown.space)="stopTerminationMarkerKeyboardEvent($event)"
                              >
                              <mat-icon aria-hidden="true">{{ terminationCase.lifecycleStatus === 'scheduled' ? 'event' : (terminationReviewLabel(terminationCase) === 'Termination review' ? 'assignment_late' : 'rule') }}</mat-icon>
                              {{ terminationReviewLabel(terminationCase) }}
                            </button>
                          }
                          <div class="member-status">{{ memberStatus(m) }}</div>
                          <button
                            mat-stroked-button
                            type="button"
                            class="member-view-btn"
                            (click)="onSelectedTeamMemberDetails(m); $event.stopPropagation()"
                            >
                            View/Edit
                          </button>
                          <div class="member-actions">
                            <button
                              mat-icon-button
                              type="button"
                              (click)="$event.stopPropagation()"
                              [matMenuTriggerFor]="selectedTeamMemberActionsMenu"
                              [matMenuTriggerData]="{ member: m }"
                              aria-label="Open employee actions"
                              >
                              <mat-icon>more_horiz</mat-icon>
                            </button>
                          </div>
                        </div>
                      </div>
                    }
                    <mat-menu #selectedTeamMemberActionsMenu="matMenu">
                      <ng-template matMenuContent let-member="member">
                        <button
                          mat-menu-item
                          type="button"
                          (click)="onSendOneTimeRewardToSelectedTeamMember(member)"
                          [disabled]="!canSendOneTimeRewardToSelectedTeamMember(member)"
                          >
                          <mat-icon>card_giftcard</mat-icon>
                          <span>One Time Reward</span>
                        </button>
                        @if (selectedTeamCanUseIndividualMemberRemoval) {
                          <mat-divider></mat-divider>
                          @if (selectedTeamCanUseManualMemberTools) {
                            <button
                              mat-menu-item
                              type="button"
                              (click)="onResendSelectedTeamMemberInvite(member)"
                              >
                              <mat-icon>mail</mat-icon>
                              <span>Resend email</span>
                            </button>
                          }
                          <button
                            mat-menu-item
                            type="button"
                            class="member-actions__danger"
                            (click)="onRemoveSelectedTeamMember(member)"
                            [disabled]="isSelectedTeamOwnerMember(member)"
                            >
                            <mat-icon>person_remove</mat-icon>
                            <span>{{ selectedTeamMemberRemovalLabel(member) }}</span>
                          </button>
                        }
                      </ng-template>
                    </mat-menu>
                    @if (!selectedTeamMembers.length) {
                      <div class="empty">
                        No team members found.
                      </div>
                    }
                    <div class="employee-rewards-actions">
                      <div class="employee-rewards-actions__label">REWARD ACTIONS</div>
                      <div class="employee-rewards-actions__buttons">
                        <button
                          mat-stroked-button
                          type="button"
                          (click)="onSendOneTimeRewardsToSelectedTeam()"
                          [disabled]="!canSendOneTimeRewardsToSelectedTeam"
                          [matTooltip]="selectedTeamOneTimeRewardDisabledReason"
                          >
                          <mat-icon>card_giftcard</mat-icon>
                          One-Time Reward
                        </button>
                        <button
                          mat-stroked-button
                          type="button"
                          (click)="onUploadRewardsToSelectedTeam()"
                          [disabled]="!canUploadRewardsToSelectedTeam"
                          [matTooltip]="selectedTeamPaymentRewardDisabledReason"
                          >
                          <mat-icon>upload_file</mat-icon>
                          Upload Rewards
                        </button>
                        <button
                          mat-stroked-button
                          type="button"
                          (click)="onTopUpSelectedTeamP2p()"
                          [disabled]="!canTopUpSelectedTeamP2p"
                          [matTooltip]="selectedTeamPaymentRewardDisabledReason"
                          >
                          <mat-icon>account_balance_wallet</mat-icon>
                          Member-to-Member Top Up
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </mat-expansion-panel>
              <!-- Dashboard grid -->
              <div class="dash-grid">
                <!-- Benefits -->
                <mat-card class="dash-card">
                  <div class="dash-header">
                    <div class="dash-left">
                      <div class="icon-square green">
                        <mat-icon>redeem</mat-icon>
                      </div>
                      <div>
                        <div class="dash-title">Benefits</div>
                        <div class="dash-sub">Assigned wellness benefits</div>
                      </div>
                    </div>
                  </div>
                  <div class="metrics">
                    <div class="metric">
                      <div class="metric-value">{{ selectedTeamAvailableProductsCount }}</div>
                      <div class="metric-label">Available Products</div>
                    </div>
                    <div class="metric">
                      <div class="metric-value">{{ selectedTeamReimbursedCount }}</div>
                      <div class="metric-label">Reimbursed</div>
                    </div>
                    <div class="metric">
                      <div class="metric-value">${{ selectedTeamWelcomeAmount }}</div>
                      <div class="metric-label">Welcome</div>
                    </div>
                    <div class="metric">
                      <div class="metric-value">${{ selectedTeamBirthdayAmount }}</div>
                      <div class="metric-label">Birthday</div>
                    </div>
                    <div class="metric">
                      <div class="metric-value">${{ selectedTeamAnniversaryAmount }}</div>
                      <div class="metric-label">Anniversary</div>
                    </div>
                  </div>
                  <div class="dash-footer">
                    <button mat-stroked-button (click)="onAddEditBenefits()">Add/Edit Benefits</button>
                  </div>
                </mat-card>
                <!-- Invites -->
                <mat-card class="dash-card">
                  <div class="dash-header">
                    <div class="dash-left">
                      <div class="icon-square blue">
                        <mat-icon>mail</mat-icon>
                      </div>
                      <div>
                        <div class="dash-title">Invites</div>
                        <div class="dash-sub">Pending and sent invitations</div>
                      </div>
                    </div>
                  </div>
                  <div class="metrics">
                    <div class="metric">
                      <div class="metric-value">{{ invitesNotInvitedCount }}</div>
                      <div class="metric-label">Not Invited</div>
                    </div>
                    <div class="metric">
                      <div class="metric-value">{{ invitesSentCount }}</div>
                      <div class="metric-label">Sent</div>
                    </div>
                    <div class="metric">
                      <div class="metric-value">{{ loggedInCount }}</div>
                      <div class="metric-label">Logged In</div>
                    </div>
                  </div>
                  <div class="invite-automation-summary">
                    {{ inviteAutomationDashboardSummary }}
                  </div>
                  <div class="invites-footer">
                    <button
                      mat-stroked-button
                      (click)="onSendEditInvites()"
                      [disabled]="hasPendingGroupProductOnboarding"
                      [matTooltip]="hasPendingGroupProductOnboarding ? pendingGroupProductInviteBlockMessage : ''"
                      >
                      Send/Edit Emails
                    </button>
                  </div>
                </mat-card>
                <!-- Snacks -->
                @if (canShowNativeSnacksActions) {
                  <mat-card class="dash-card snacks-dash-card">
                    <div class="dash-header">
                      <div class="dash-left">
                        <div class="icon-square blue">
                          <mat-icon>campaign</mat-icon>
                        </div>
                        <div>
                          <div class="dash-title">Snacks</div>
                          <div class="dash-sub">Explore panel visibility</div>
                        </div>
                      </div>
                    </div>
                    <div class="snacks-info">
                      <mat-icon>info_outline</mat-icon>
                      <span>
                        Snacks appear as small clickable panels on the customer app Explore page. Enabled snacks are visible to this team's employees and open into a detail page.
                      </span>
                    </div>
                    <div class="team-snacks-summary">
                      <div>
                        <div class="team-snacks-title">{{ selectedTeamSnackSummaryTitle }}</div>
                        <div class="team-snacks-subtitle">{{ selectedTeamSnackSummarySubtitle }}</div>
                      </div>
                      @if (selectedTeamSnackPreviewRewards.length > 0) {
                        <div class="team-snack-preview-list">
                          @for (reward of selectedTeamSnackPreviewRewards; track reward) {
                            <div class="team-snack-preview">
                              @if (reward.imageUrl) {
                                <img [src]="reward.imageUrl" [alt]="reward.title" />
                              }
                              @if (!reward.imageUrl) {
                                <mat-icon>campaign</mat-icon>
                              }
                            </div>
                          }
                        </div>
                      }
                    </div>
                    <div class="dash-footer">
                      <button
                        mat-stroked-button
                        [disabled]="isSelectedTeamSnacksLoading"
                        (click)="onEditSelectedTeamSnacks()">
                        Edit Snacks
                      </button>
                    </div>
                  </mat-card>
                }
                <mat-card class="dash-card rewards-activity-card">
                  <button
                    type="button"
                    class="rewards-activity-toggle"
                    (click)="toggleRewardsActivityExpanded()"
                    [attr.aria-expanded]="isRewardsActivityExpanded"
                    >
                    <div class="dash-left">
                      <div class="icon-square rose">
                        <mat-icon>card_giftcard</mat-icon>
                      </div>
                      <div>
                        <div class="dash-title">Rewards Activity</div>
                        <div class="dash-sub">Automated bonuses and sent team rewards for this team</div>
                      </div>
                    </div>
                    <mat-icon class="rewards-activity-toggle-icon">
                      {{ isRewardsActivityExpanded ? 'expand_less' : 'expand_more' }}
                    </mat-icon>
                  </button>
                  @if (isRewardsActivityExpanded) {
                    <div class="rewards-activity-body">
                      @if (isRewardsActivityLoading) {
                        <div class="rewards-activity-status">
                          Loading rewards activity...
                        </div>
                      }
                      @if (!isRewardsActivityLoading && rewardsActivityError) {
                        <div class="rewards-activity-error">
                          {{ rewardsActivityError }}
                        </div>
                      }
                      <div class="rewards-activity-section">
                        <div class="rewards-activity-section__title">Automated Bonus History</div>
                        @if (!automatedBonusHistory.length) {
                          <div class="rewards-activity-empty">
                            No automatic bonuses have been paid
                          </div>
                        }
                        @if (automatedBonusHistory.length) {
                          <div class="rewards-activity-table-wrap">
                            <table class="rewards-activity-table">
                              <tr class="rewards-activity-table__header">
                                <th>Sent To</th>
                                <th>Type</th>
                                <th>Amount</th>
                                <th>Date Sent</th>
                              </tr>
                              @for (bonus of automatedBonusHistory; track bonus) {
                                <tr>
                                  <td>{{ bonus.sentTo }}</td>
                                  <td>{{ bonus.type }}</td>
                                  <td>{{ bonus.amount | currency }}</td>
                                  <td>{{ bonus.sentOn | date }}</td>
                                </tr>
                              }
                            </table>
                          </div>
                        }
                      </div>
                      <div class="rewards-activity-section">
                        <div class="rewards-activity-section__title">One Time Rewards History</div>
                        @if (!oneTimeRewardsHistory.length) {
                          <div class="rewards-activity-empty">
                            There are no sent One Time Rewards
                          </div>
                        }
                        @if (oneTimeRewardsHistory.length) {
                          <div class="rewards-activity-table-wrap">
                            <table class="rewards-activity-table">
                              <tr class="rewards-activity-table__header">
                                <th>Sent on</th>
                                <th>Amount</th>
                                <th>Message</th>
                                <th>Recipient</th>
                              </tr>
                              @for (reward of oneTimeRewardsHistory; track reward) {
                                <tr>
                                  <td>{{ reward.sentOn ? (reward.sentOn | date) : '---' }}</td>
                                  <td>{{ reward.amount | currency }}</td>
                                  <td>{{ reward.message }}</td>
                                  <td>{{ reward.recipient }}</td>
                                </tr>
                              }
                            </table>
                          </div>
                        }
                      </div>
                    </div>
                  }
                </mat-card>
              </div>
            </div>
          }
        } @else {
          <section class="teams-empty-state">
            <div class="teams-empty-state__icon">
              <mat-icon>group_add</mat-icon>
            </div>
            <h2>No teams created yet</h2>
            @if (isHealthyPayrollConnection) {
              <p>
                Create your first team to view and manage team details.
              </p>
            } @else {
              <p>Create your first team now, then manage members manually from this page.</p>
            }
            @if (canUseLegacyCensus) {
              <button mat-raised-button color="primary" type="button" (click)="onCreateTeam()">
                <mat-icon>add</mat-icon>
                Create new team
              </button>
            }
          </section>
        }
      </mat-tab>
      <mat-tab label="All Employees">
        @if (!canUseLegacyCensus) {
          <app-payroll-workforce-employees [state]="workforce" [creation]="teamCreation" [draft]="allEmployeesDirectory" [active]="selectedTabIndex === 2"
            (createTeam)="onCreateEmployeeSelection($event)"></app-payroll-workforce-employees>
        }
        @if (canUseLegacyCensus) {
          <div class="all-employees">
            @if (isPayrollConnectionErrored || hasAllEmployeesConnectionError) {
              <div class="payroll-connection-error">
                <div class="error-content">
                  <mat-icon>error_outline</mat-icon>
                  <div class="error-copy">
                    <div class="error-title">{{ allEmployeesConnectionErrorTitle }}</div>
                    <div class="error-message">
                      {{ allEmployeesConnectionErrorMessage }}
                    </div>
                  </div>
                </div>
                @if (shouldShowAllEmployeesConnectionAction) {
                  <button mat-raised-button color="primary" type="button" (click)="onGoToCompanyConnections()">
                    Go to Company Connections
                  </button>
                }
              </div>
            }
            @if (!isCompanyScopedWorkspace && isPayrollConnectionMissing) {
              <div class="setup-cta setup-cta-inline">
                <div class="setup-cta-content">
                  <mat-icon>link_off</mat-icon>
                  <div class="setup-cta-copy">
                    <h2>{{ payrollImportUnavailableTitle }}</h2>
                    <p>{{ payrollImportUnavailableMessage }}</p>
                  </div>
                </div>
                @if (!isCompanyScopedWorkspace) {
                  <button mat-raised-button color="primary" type="button" (click)="onGoToCompanyConnections()">
                    Go to Company Connections
                  </button>
                }
              </div>
            }
            @if (allEmployeesReadOnlyBannerMessage) {
              <div class="manual-all-employees-banner readonly-provider-banner">
                {{ allEmployeesReadOnlyBannerMessage }}
              </div>
            }
            @if (allEmployeesCensusWarningMessage) {
              <div class="manual-all-employees-banner">
                {{ allEmployeesCensusWarningMessage }}
              </div>
            }
            @if (allEmployeesRefreshError) {
              <div class="manual-all-employees-banner refresh-error-banner" role="alert">
                {{ allEmployeesRefreshError }}
              </div>
            }
            @if (canUseProviderCensus && !hasAllEmployeesConnectionError) {
              <mat-card class="filter-card">
                <div class="filter-title">{{ allEmployeesFilterCardTitle }}</div>
                <mat-form-field appearance="outline" class="search">
                  <mat-icon matPrefix>search</mat-icon>
                  <input
                    matInput
                    placeholder="Search employees by name or email..."
                    [(ngModel)]="searchAllEmployees"
                    />
                </mat-form-field>
                @if (canUseAllEmployeesFilterControls) {
                  <div class="filter-row">
                    @for (filter of allEmployeesAvailableFilters; track filter) {
                      <mat-form-field appearance="outline" class="filter">
                        <mat-select
                          class="payroll-filter-select"
                          [placeholder]="filter[0]"
                          [value]="allEmployeesActiveFiltersMap[filter[0]]"
                          (selectionChange)="handleFilterSelect(filter[0], $event.value)"
                          panelClass="payroll-filter-select-panel"
                          multiple
                          >
                          <mat-select-trigger>
                            <span class="filter-trigger">
                              <span class="filter-trigger-label">{{ filter[0] }}</span>
                              <span class="filter-trigger-count">{{ activeFilterCount(filter[0]) }}</span>
                            </span>
                          </mat-select-trigger>
                          @for (opt of filter[1]; track opt) {
                            <mat-option [value]="opt">
                              {{ displayFilterValue(filter[0], opt) }}
                            </mat-option>
                          }
                        </mat-select>
                      </mat-form-field>
                    }
                  </div>
                }
                @if (canUseAllEmployeesFilterControls && allEmployeesActiveFilters.length) {
                  <div class="applied-row">
                    <mat-chip-listbox class="chips">
                      @for (f of allEmployeesActiveFilters; track f) {
                        <mat-chip-option
                          selected
                          removable
                          (removed)="clearFilter(f.key, f.value)"
                          >
                          <span class="chip-label">{{ f.label }}: {{ displayFilterValue(f.label, f.value) }}</span>
                          <button matChipRemove class="chip-x" aria-label="Remove filter">
                            <mat-icon>close</mat-icon>
                          </button>
                        </mat-chip-option>
                      }
                    </mat-chip-listbox>
                    <button mat-button class="clear-all" (click)="clearAllFilters()">Clear all</button>
                  </div>
                }
              </mat-card>
            }
            @if (isUsingCompanyMemberFallbackForAllEmployees) {
              <div class="manual-all-employees-banner">
                {{ allEmployeesFallbackBannerMessage }}
              </div>
            }
            @if (isUsingCompanyMemberFallbackForAllEmployees) {
              <mat-card class="filter-card">
                <div class="filter-title">SEARCH EMPLOYEE LIST</div>
                <mat-form-field appearance="outline" class="search">
                  <mat-icon matPrefix>search</mat-icon>
                  <input
                    matInput
                    placeholder="Search employees by name or email..."
                    [(ngModel)]="searchAllEmployees"
                    />
                </mat-form-field>
              </mat-card>
            }
            <div class="all-meta">
              <div class="left">
                <div class="meta-title">Available employees</div>
                <div class="count-pill">{{ allEmployeesVisibleCount }} of {{ allEmployeesTotalCount }}</div>
              </div>
              <div class="right">
                @if (canRefreshAllEmployeesFromUkg) {
                  <button
                    mat-stroked-button
                    class="refresh-from-ukg"
                    type="button"
                    (click)="refreshAllEmployeesFromUkg()"
                    [disabled]="isRefreshingAllEmployeesFromUkg"
                    >
                    <mat-icon>refresh</mat-icon>
                    {{ isRefreshingAllEmployeesFromUkg ? 'Refreshing...' : 'Refresh from UKG' }}
                  </button>
                }
                <div class="selected-label">{{ selectedCount }} selected</div>
                <button
                  mat-stroked-button
                  class="create-from-selection"
                  (click)="onCreateTeamFromSelection()"
                  [disabled]="selectedCount === 0 || !canCreateTeamFromSelection"
                  [matTooltip]="createTeamFromSelectionDisabledTooltip"
                  >
                  <mat-icon>add</mat-icon>
                  Create team from selection
                </button>
              </div>
            </div>
            <div
              class="table-wrap"
              [class.is-loading]="isAllEmployeesResultsLoading"
              [attr.aria-busy]="isAllEmployeesResultsLoading"
              >
              @if (isAllEmployeesResultsLoading) {
                <div class="results-loading-overlay" aria-live="polite">
                  <mat-spinner [diameter]="30"></mat-spinner>
                  <div class="results-loading-copy">Applying filters...</div>
                </div>
              }
              <div class="table-scroll-hint">Scroll to see more columns</div>
              <table
                mat-table
                matSort
                [matSortDisabled]="!isPaycorProviderCensus"
                [matSortActive]="allEmployeesSort.active"
                [matSortDirection]="allEmployeesSort.direction"
                (matSortChange)="onAllEmployeesSortChange($event)"
                [dataSource]="filteredEmployees"
                class="employees-table desktop-table"
                >
                <!-- Select -->
                <ng-container matColumnDef="select">
                  <th mat-header-cell *matHeaderCellDef class="col-select">
                    <mat-checkbox
                      [checked]="isAllVisibleSelected()"
                      [indeterminate]="isSomeVisibleSelected()"
                      (change)="toggleSelectAllVisible($event.checked)"
                      [disabled]="isAllEmployeesResultsLoading || !canCreateTeamFromSelection"
                      aria-label="Select all visible employees"
                    ></mat-checkbox>
                  </th>
                  <td mat-cell *matCellDef="let e" class="col-select">
                    <mat-checkbox
                      [checked]="selectedEmails[employeeKey(e)]"
                      (change)="toggleEmployee(employeeKey(e), $event.checked)"
                      [disabled]="isAllEmployeesResultsLoading || !canCreateTeamFromSelection || !hasEmployeeEmail(e)"
                      [matTooltip]="missingEmployeeEmailTooltip(e)"
                      aria-label="Select employee"
                    ></mat-checkbox>
                  </td>
                </ng-container>
                <!-- Employee -->
                <ng-container matColumnDef="employee">
                  <th mat-header-cell *matHeaderCellDef mat-sort-header>EMPLOYEE</th>
                  <td mat-cell *matCellDef="let e">
                    <div class="emp-cell">
                      <div class="emp-avatar">
                        <mat-icon>person</mat-icon>
                      </div>
                      <div class="emp-text">
                        <div class="emp-name">{{ employeeName(e) }}</div>
                        @if (!hasEmployeeEmail(e)) {
                          <div
                            class="missing-email-alert"
                            matTooltip="Add an email address in the payroll provider before importing this employee to Wallit."
                            >
                            <mat-icon>warning_amber</mat-icon>
                            <span>Missing email address. Cannot import to Wallit.</span>
                          </div>
                        } @else {
                          <div class="emp-email">{{ employeeEmail(e) }}</div>
                        }
                      </div>
                    </div>
                  </td>
                </ng-container>
                <ng-container matColumnDef="status">
                  <th mat-header-cell *matHeaderCellDef mat-sort-header>STATUS</th>
                  <td mat-cell *matCellDef="let e">
                    <span class="pill subtle">{{ employeeStatus(e) }}</span>
                  </td>
                </ng-container>
                <ng-container matColumnDef="jobTitle">
                  <th mat-header-cell *matHeaderCellDef mat-sort-header>JOB TITLE</th>
                  <td mat-cell *matCellDef="let e">
                    <span class="pill">{{ employeeJobTitle(e) }}</span>
                  </td>
                </ng-container>
                <ng-container matColumnDef="location">
                  <th mat-header-cell *matHeaderCellDef mat-sort-header>LOCATION</th>
                  <td mat-cell *matCellDef="let e">
                    <span class="pill">{{ employeeLocation(e) }}</span>
                  </td>
                </ng-container>
                <ng-container matColumnDef="division">
                  <th mat-header-cell *matHeaderCellDef mat-sort-header>DIVISION</th>
                  <td mat-cell *matCellDef="let e">
                    <span class="pill">{{ employeeDivision(e) }}</span>
                  </td>
                </ng-container>
                <ng-container matColumnDef="department">
                  <th mat-header-cell *matHeaderCellDef mat-sort-header>DEPARTMENT</th>
                  <td mat-cell *matCellDef="let e">
                    <span class="pill">{{ employeeDepartment(e) }}</span>
                  </td>
                </ng-container>
                <ng-container matColumnDef="manager">
                  <th mat-header-cell *matHeaderCellDef mat-sort-header>MANAGER</th>
                  <td mat-cell *matCellDef="let e">
                    <span class="pill">{{ employeeManager(e) }}</span>
                  </td>
                </ng-container>
                <ng-container matColumnDef="payGroup">
                  <th mat-header-cell *matHeaderCellDef mat-sort-header>PAY GROUP</th>
                  <td mat-cell *matCellDef="let e">
                    <span class="pill grey">{{ employeePayGroup(e) }}</span>
                  </td>
                </ng-container>
                <ng-container matColumnDef="workerType">
                  <th mat-header-cell *matHeaderCellDef mat-sort-header>WORKER TYPE</th>
                  <td mat-cell *matCellDef="let e">
                    <span class="pill accent">{{ employeeWorkerType(e) }}</span>
                  </td>
                </ng-container>
                <ng-container matColumnDef="state">
                  <th mat-header-cell *matHeaderCellDef mat-sort-header>STATE</th>
                  <td mat-cell *matCellDef="let e">
                    <span class="pill subtle">{{ employeeState(e) }}</span>
                  </td>
                </ng-container>
                <ng-container matColumnDef="employmentType">
                  <th mat-header-cell *matHeaderCellDef mat-sort-header>EMPLOYMENT</th>
                  <td mat-cell *matCellDef="let e">
                    <span class="pill accent">{{ employeeEmploymentType(e) }}</span>
                  </td>
                </ng-container>
                <ng-container matColumnDef="payType">
                  <th mat-header-cell *matHeaderCellDef mat-sort-header>PAY TYPE</th>
                  <td mat-cell *matCellDef="let e">
                    <span class="pill grey">{{ employeePayType(e) }}</span>
                  </td>
                </ng-container>
                <ng-container matColumnDef="actions">
                  <th mat-header-cell *matHeaderCellDef class="col-actions">ACTIONS</th>
                  <td mat-cell *matCellDef="let e" class="col-actions">
                    <button
                      mat-raised-button
                      class="details-btn"
                      (click)="onEmployeeDetails(e)"
                      [disabled]="isAllEmployeesResultsLoading"
                      >
                      <mat-icon>visibility</mat-icon>
                      <span class="details-label">Details</span>
                    </button>
                  </td>
                </ng-container>
                <tr mat-header-row *matHeaderRowDef="allEmployeesColumns"></tr>
                <tr mat-row *matRowDef="let row; columns: allEmployeesColumns"></tr>
              </table>
              <div class="mobile-employees-list" aria-label="Available employees">
                <div class="mobile-select-all">
                  <mat-checkbox
                    [checked]="isAllVisibleSelected()"
                    [indeterminate]="isSomeVisibleSelected()"
                    (change)="toggleSelectAllVisible($event.checked)"
                    [disabled]="isAllEmployeesResultsLoading || !canCreateTeamFromSelection"
                    aria-label="Select all visible employees"
                    >
                    Select visible employees
                  </mat-checkbox>
                </div>
                @for (e of filteredEmployees; track e) {
                  <article class="mobile-employee-card">
                    <div class="mobile-employee-top">
                      <mat-checkbox
                        class="mobile-employee-checkbox"
                        [checked]="selectedEmails[employeeKey(e)]"
                        (change)="toggleEmployee(employeeKey(e), $event.checked)"
                        [disabled]="isAllEmployeesResultsLoading || !canCreateTeamFromSelection || !hasEmployeeEmail(e)"
                        [matTooltip]="missingEmployeeEmailTooltip(e)"
                        aria-label="Select employee"
                      ></mat-checkbox>
                      <div class="emp-cell mobile-emp-cell">
                        <div class="emp-avatar">
                          <mat-icon>person</mat-icon>
                        </div>
                        <div class="emp-text">
                          <div class="emp-name">{{ employeeName(e) }}</div>
                          @if (!hasEmployeeEmail(e)) {
                            <div
                              class="missing-email-alert"
                              matTooltip="Add an email address in the payroll provider before importing this employee to Wallit."
                              >
                              <mat-icon>warning_amber</mat-icon>
                              <span>Missing email address. Cannot import to Wallit.</span>
                            </div>
                          } @else {
                            <div class="emp-email">{{ employeeEmail(e) }}</div>
                          }
                        </div>
                      </div>
                      <button
                        mat-raised-button
                        class="details-btn mobile-details-btn"
                        (click)="onEmployeeDetails(e)"
                        [disabled]="isAllEmployeesResultsLoading"
                        >
                        <mat-icon>visibility</mat-icon>
                        <span class="details-label">Details</span>
                      </button>
                    </div>
                    <div class="mobile-pills-row">
                      <span class="pill">{{ employeeLocation(e) }}</span>
                      @if (!isPaycorProviderCensus) {
                        <span class="pill">{{ employeeDivision(e) }}</span>
                      }
                      @if (isPaycorProviderCensus) {
                        <span class="pill">{{ employeeJobTitle(e) }}</span>
                      }
                    </div>
                    <details class="mobile-employee-details">
                      <summary>View details</summary>
                      @if (isPaycorProviderCensus) {
                        <div class="mobile-field-row">
                          <span class="mobile-field-label">Status</span>
                          <span class="mobile-field-value">{{ employeeStatus(e) }}</span>
                        </div>
                        <div class="mobile-field-row">
                          <span class="mobile-field-label">Manager</span>
                          <span class="mobile-field-value">{{ employeeManager(e) }}</span>
                        </div>
                        <div class="mobile-field-row">
                          <span class="mobile-field-label">Pay Group</span>
                          <span class="mobile-field-value">{{ employeePayGroup(e) }}</span>
                        </div>
                        <div class="mobile-field-row">
                          <span class="mobile-field-label">Worker Type</span>
                          <span class="mobile-field-value">{{ employeeWorkerType(e) }}</span>
                        </div>
                      }
                      <div class="mobile-field-row">
                        <span class="mobile-field-label">Department</span>
                        <span class="mobile-field-value">{{ employeeDepartment(e) }}</span>
                      </div>
                      @if (!isPaycorProviderCensus) {
                        <div class="mobile-field-row">
                          <span class="mobile-field-label">State</span>
                          <span class="mobile-field-value">{{ employeeState(e) }}</span>
                        </div>
                      }
                      @if (!isPaycorProviderCensus) {
                        <div class="mobile-field-row">
                          <span class="mobile-field-label">Employment</span>
                          <span class="mobile-field-value">{{ employeeEmploymentType(e) }}</span>
                        </div>
                      }
                      @if (!isPaycorProviderCensus) {
                        <div class="mobile-field-row">
                          <span class="mobile-field-label">Pay Type</span>
                          <span class="mobile-field-value">{{ employeePayType(e) }}</span>
                        </div>
                      }
                    </details>
                  </article>
                }
              </div>
            </div>
            @if (!filteredEmployees.length && !isLoadingInitialData && !isAllEmployeesResultsLoading) {
              <div class="empty-state-card">
                <mat-icon>groups</mat-icon>
                <h3>No employees to show</h3>
                @if (isUsingCompanyMemberFallbackForAllEmployees) {
                  <p>
                    No existing team members have been added for this company yet.
                  </p>
                } @else {
                  No employees match the current search or filters.
                }
              </div>
            }
          </div>
        }
      </mat-tab>
    </mat-tab-group>
  }

</div>
